import _ from "lodash/fp";
import $ from "jquery";

const Joi = require("joi");

const filterByConfigurationID = (configurationID) =>
  _.filter(({ configurations }) => configurations.includes(configurationID));

const filterByObjectValues = (object) => (configuration) => (filters) => {
  return (
    !filters.length ||
    filters.some(({ test, values = [] }) => {
      if (!test) return false;
      switch (test) {
        case "insertedInteractions":
          const contentElement = window.document.createElement("div");
          contentElement.innerHTML = object.content;
          const insertedInteractions = _.uniqWith(_.isEqual)(
            $(contentElement)
              .find(".interaction")
              .map((i, el) => ({
                name: $(el).attr("data-interaction-name"),
                type: $(el).attr("data-interaction-type"),
              }))
              .get(),
          );
          return values.some((value) => {
            const [type, number] = value.split(":");
            const count = parseInt(number);
            const orMore = /\+$/.test(number);
            return (
              (insertedInteractions.length === count ||
                (orMore && insertedInteractions.length >= count)) &&
              (type === "Any" ||
                insertedInteractions.every(
                  (interaction) => interaction.type === type,
                ))
            );
          });
        default:
          // NOTE: This is for the simplest cases (e.g., testing a value like
          // 'contentArea').  We'll need to get more complicated as different
          // filters are added.
          if (!object[test]) return false;
          const allowedValues = _.pipe([
            filterOptionsByObjectValues(object)(configuration),
            _.map((o) => o.value),
          ])(configuration[test].options);
          return (
            !allowedValues.includes(object[test]) ||
            values.includes(object[test])
          );
      }
    })
  );
};

const filterFieldsByObjectValues =
  (object) =>
  (configuration) =>
  (fields = []) => {
    return fields.filter((path) => {
      if (!configuration[path]) return false;
      const { filters = [] } = configuration[path];
      return filterByObjectValues(object)(configuration)(filters);
    });
  };

const filterOptionsByObjectValues =
  (object) => (configuration) => (options) => {
    return options.filter(({ filters = [] }) =>
      filterByObjectValues(object)(configuration)(filters),
    );
  };

const getOptions =
  (type) =>
  (readonly) =>
  (users) =>
  (program) =>
  (options = []) => {
    const { id: programID, configurationID } = program;
    return _.pipe([
      filterByConfigurationID(configurationID),
      getOptionValue(type)(readonly)(users)(programID),
    ])(options);
  };

const getOptionValue =
  (type) => (readonly) => (users) => (programID) => (values) => {
    if (type === "user") {
      const roles = values.length ? values[0].roles : null;
      const filteredUsers = getUserOptions(users)(programID)(readonly)(roles);
      return _.pipe([
        _.sortBy(["name"]),
        _.map((user) => ({ ...user, label: user.name, value: user.id })),
      ])(filteredUsers);
    } else {
      return _.reduce((acc, option) => {
        const filters = option.filters.map(
          ({ configurations, ...rest }) => rest,
        );
        const value = getValue(option);
        return [
          ...acc,
          {
            filters,
            label: value,
            value,
          },
        ];
      }, [])(values);
    }
  };

const getRHFFieldOptionLabel =
  (options = []) =>
  (value = "") => {
    return options.find((o) => o.value === value)?.label;
  };

const getUserOptions = (users) => (programID) => (readonly) => (roles) => {
  return users.reduce((acc, user) => {
    // If it's a readonly field, then we might as well match all users
    if (readonly) return [...acc, { ...user, disabled: true }];

    const access = user.programAccess.find(({ program }) => {
      return program === programID;
    });
    if (!access) return [...acc];

    const disabled =
      !user.azureId || _.intersection(access.reviewTypes, roles).length === 0;
    return [...acc, { ...user, disabled }];
  }, []);
};

const getValue = ({ booleanValue, intValue, stringValue }) => {
  if (!_.isNull(booleanValue)) {
    return booleanValue;
  } else if (!_.isNull(intValue)) {
    return intValue;
  } else if (!_.isNull(stringValue)) {
    return stringValue;
  } else {
    return null;
  }
};

const normalizeConfiguration =
  (objectType) =>
  (configuration = { item: [], passage: [] }) =>
  (users = []) =>
  (program) => {
    const { configurationID } = program;
    return configuration[objectType].reduce((acc, field) => {
      const { constraints, label, path, type, values } = field;
      const constraint = _.pipe([
        filterByConfigurationID(configurationID),
        _.head,
      ])(constraints);
      if (!constraint) return { ...acc };
      const {
        disablePlaceholder,
        filters,
        maxLength,
        multiline,
        readonly,
        required,
      } = constraint;
      return {
        ...acc,
        [path]: {
          disablePlaceholder,
          filters,
          label,
          options: getOptions(type)(readonly)(users)(program)(values),
          readonly,
          rules: {
            maxLength,
            multiline,
            required: !["switch"].includes(type) && !readonly && required,
          },
          type,
        },
      };
    }, {});
  };

/**
 * Takes a set of rules appropriate to react-hook-form inputs and provides default messaging where appropriate
 *
 * @param {Object} rules
 * @returns Object
 */
const provideDefaultMessages = (rules = {}) => {
  return Object.keys(rules).reduce((acc, key) => {
    switch (key) {
      case "max":
        return {
          ...acc,
          [key]: ["number", "string"].includes(typeof rules[key])
            ? {
                message: `This field must have a value of ${rules[key]} or less`,
                value: parseInt(rules[key]),
              }
            : rules[key],
        };
      case "maxLength":
        return {
          ...acc,
          [key]: ["number", "string"].includes(typeof rules[key])
            ? {
                message: `This field must be ${rules[key]} or fewer characters long`,
                value: parseInt(rules[key]),
              }
            : rules[key],
        };
      case "min":
        return {
          ...acc,
          [key]: ["number", "string"].includes(typeof rules[key])
            ? {
                message: `This field must have a value of ${rules[key]} or more`,
                value: parseInt(rules[key]),
              }
            : rules[key],
        };
      case "minLength":
        return {
          ...acc,
          [key]: ["number", "string"].includes(typeof rules[key])
            ? {
                message: `This field must be ${rules[key]} or more characters long`,
                value: parseInt(rules[key]),
              }
            : rules[key],
        };
      case "required":
        return {
          ...acc,
          [key]:
            ["boolean"].includes(typeof rules[key]) && rules[key]
              ? `This field is required`
              : rules[key],
        };
      default:
        return { ...acc, [key]: rules[key] };
    }
  }, {});
};

// this builds the Joi schema for validation, this works for item and passage configurations
const transformConfigToSchema = (config) => {
  if (!config) return;

  const schema = Object.keys(config)
    .filter((key) => !config[key].readonly)
    .reduce((acc, key) => {
      const params = config[key];

      let validation = Joi;
      switch (params.type) {
        case "date":
        case "datetime":
          validation = validation.date();
          break;
        case "integer":
          validation = validation.number();
          if (params.rules.maxLength)
            validation = validation.max(10 ** params.rules.maxLength - 1);
          break;
        case "multipleSelect":
          if (
            params.options.length !== 0 &&
            params.options.every(
              ({ value }) =>
                Number.isInteger(value) || parseInt(value).toString() === value,
            )
          ) {
            validation = validation.array().items(Joi.number());
          } else {
            validation = validation.array().items(Joi.string());
          }
          break;
        case "select":
          if (
            params.options.length !== 0 &&
            params.options.every(
              ({ value }) =>
                Number.isInteger(value) || parseInt(value).toString() === value,
            )
          ) {
            validation = validation.number();
          } else {
            validation = validation.string();
          }
          break;
        case "switch":
          validation = validation.boolean();
          break;
        case "text":
          validation = validation.string();
          if (params.rules.maxLength)
            validation = validation.max(params.rules.maxLength);
          break;
        case "user":
          validation = validation
            .string()
            .uuid({ separator: "-", version: ["uuidv4"] });
          break;
        default:
          validation = validation.any();
          console.warn(`Invalid validation type: ${key} -> %o`, config[key]);
      }

      validation = params.rules.required
        ? validation.empty(Joi.any().valid("", null)).required()
        : validation.allow("", null);

      return { ...acc, [key]: validation.label(config[key].label) };
    }, {});

  return Joi.object(schema).options({
    abortEarly: false,
    allowUnknown: true,
    errors: { wrap: { label: false } },
  });
};

const validateWorkspaceValues =
  (schemas) =>
  ({ item, passage }) => {
    const itemErrors =
      schemas.item
        ?.keys({
          interactions: Joi.array().items(
            Joi.alternatives().conditional(".__typename", {
              switch: [
                {
                  is: "ChoiceInteraction",
                  then: Joi.object({
                    foils: Joi.array().items(
                      Joi.object({
                        content: Joi.string().allow(""),
                        correct: Joi.boolean(),
                        distractorRationale: Joi.string().allow(""),
                      }),
                    ),
                    maxChoices: Joi.number()
                      .greater(
                        Joi.ref("foils", {
                          adjust: (foils) => {
                            return (
                              foils.filter((foil) => foil.correct === true)
                                .length - 1
                            );
                          },
                        }),
                      )
                      .rule({
                        message:
                          "Max Choices must be greater than or equal to the number of correct foils ",
                      })
                      .min(Joi.ref("minChoices", { render: true }))
                      .required()
                      .label("Max Choices"),
                    minChoices: Joi.number().required().label("Min Choices"),
                  }),
                },
                {
                  is: "DemographicTextEntryInteraction",
                  then: Joi.object({
                    correctResponse: Joi.string().allow(""),
                    expectedLength: Joi.number()
                      .required()
                      .max(999)
                      .label("Maximum Characters"),
                    ignoreCase: Joi.boolean(),
                    patternMask: Joi.string().allow(""),
                    placeholderText: Joi.string().allow(""),
                  }),
                },
                {
                  is: "ExtendedTextInteraction",
                  then: Joi.object({
                    expectedLength: Joi.number()
                      .required()
                      .empty(null)
                      .label("Maximum Characters"),
                    expectedLines: Joi.number().allow(null),
                    scoringCriteria: Joi.string().allow(""),
                  }),
                },
                {
                  is: "GapMatchInteraction",
                  then: Joi.object({
                    elements: Joi.array().items(
                      Joi.object({
                        content: Joi.string()
                          .required()
                          .empty("")
                          .label("Elements - Content"),
                        identifier: Joi.string().required().empty(""),
                        matchGroup: Joi.string().allow(null),
                        matchMax: Joi.number()
                          .required()
                          .label("Elements - Match Max"),
                      }),
                    ),
                    targets: Joi.array().items(
                      Joi.object({
                        correctResponse: Joi.string()
                          .required()
                          .empty(Joi.any().valid("", null))
                          .label("Targets - Correct Response"),
                        identifier: Joi.string().required().empty(""),
                        matchGroup: Joi.string().allow(null),
                        width: Joi.number().required().label("Targets - Width"),
                      }),
                    ),
                  }),
                },
                {
                  is: "GraphicGapMatchInteraction",
                  then: Joi.object({
                    backgroundFileName: Joi.string().allow(""),
                    backgroundHeight: Joi.number(),
                    backgroundWidth: Joi.number(),
                    correctResponses: Joi.array().items(
                      Joi.object({
                        element: Joi.string()
                          .required()
                          .empty("")
                          .label("Correct Responses - Element"),
                        target: Joi.string()
                          .required()
                          .empty("")
                          .label("Correct Responses - Target"),
                      }),
                    ),
                    elements: Joi.array().items(
                      Joi.object({
                        identifier: Joi.string().required().empty(""),
                        imageUrl: Joi.string().allow(""),
                        matchMax: Joi.number()
                          .required()
                          .label("Elements - Match Max"),
                      }),
                    ),
                    targets: Joi.array().items(
                      Joi.object({
                        height: Joi.number(),
                        identifier: Joi.string().required().empty(""),
                        matchMax: Joi.number()
                          .required()
                          .label("Targets - Match Max"),
                        width: Joi.number(),
                        xCoord: Joi.number(),
                        yCoord: Joi.number(),
                      }),
                    ),
                  }),
                },
                {
                  is: "HotspotInteraction",
                  then: Joi.object({
                    backgroundFileName: Joi.string()
                      .required()
                      .empty("")
                      .label("Background Image"),
                    backgroundHeight: Joi.number(),
                    backgroundWidth: Joi.number(),
                    elements: Joi.array().items(
                      Joi.object({
                        colorStyle: Joi.string().allow(""),
                        correctResponse: Joi.boolean(),
                        height: Joi.number(),
                        identifier: Joi.string().required().empty(""),
                        left: Joi.number(),
                        radius: Joi.number(),
                        shape: Joi.string()
                          .required()
                          .empty("")
                          .label("Elements - Shape"),
                        top: Joi.number(),
                        width: Joi.number(),
                        xCoord: Joi.number(),
                        yCoord: Joi.number(),
                      }),
                    ),
                    maxChoices: Joi.number().required().label("Max Choices"),
                  }),
                },
                {
                  is: "InlineChoiceInteraction",
                  then: Joi.object({
                    choices: Joi.array().items(
                      Joi.object({
                        correct: Joi.boolean(),
                        text: Joi.string().allow(""),
                      }),
                    ),
                    distractorRationale: Joi.string().allow(""),
                  }),
                },
                {
                  is: "MatchInteraction",
                  then: Joi.object({
                    correctResponse: Joi.array().items(
                      Joi.object({
                        element: Joi.string()
                          .required()
                          .empty("")
                          .label("Correct Responses - Element"),
                        target: Joi.string()
                          .required()
                          .empty("")
                          .label("Correct Responses - Target"),
                      }),
                    ),
                    elements: Joi.array().items(
                      Joi.object({
                        content: Joi.string().allow(""),
                        identifier: Joi.string().required().empty(""),
                        matchGroup: Joi.string().allow(null),
                        matchMax: Joi.number()
                          .required()
                          .label("Elements - Match Max"),
                      }),
                    ),
                    maxElementsPerRow: Joi.number()
                      .required()
                      .label("Elements per Row"),
                    targets: Joi.array().items(
                      Joi.object({
                        identifier: Joi.string().required().empty(""),
                        matchGroup: Joi.string().allow(null),
                        matchMax: Joi.number()
                          .required()
                          .label("Targets - Match Max"),
                        text: Joi.string().allow(""),
                        width: Joi.number(),
                      }),
                    ),
                  }),
                },
                {
                  is: "MatchTableInteraction",
                  then: Joi.object({
                    columnChoices: Joi.array().items(
                      Joi.object({
                        content: Joi.string().required().empty(""),
                        identifier: Joi.string().required().empty(""),
                        matchMax: Joi.number()
                          .required()
                          .label("Column - Match Max"),
                        name: Joi.string()
                          .required()
                          .empty("")
                          .label("Column - Name"),
                      }),
                    ),
                    prompt: Joi.string().allow(""),
                    rowCentric: Joi.boolean(),
                    rowChoices: Joi.array().items(
                      Joi.object({
                        content: Joi.string().required().empty(""),
                        identifier: Joi.string().required().empty(""),
                        matchMax: Joi.number()
                          .required()
                          .label("Row - Match Max"),
                        construct: Joi.string()
                          .required()
                          .label("Row - Construct"),
                        name: Joi.string()
                          .required()
                          .empty("")
                          .label("Row - Name"),
                      }),
                    ),
                    templateName: Joi.string().required().empty(""),
                  }),
                },
                {
                  is: "OrderInteraction",
                  then: Joi.object({
                    correctResponse: Joi.array().items(
                      Joi.object({
                        identifier: Joi.string().required().empty(""),
                      }),
                    ),
                    elements: Joi.array().items(
                      Joi.object({
                        content: Joi.string().allow(""),
                        identifier: Joi.string().required().empty(""),
                      }),
                    ),
                    orientation: Joi.string()
                      .required()
                      .empty("")
                      .label("Orientation"),
                  }),
                },
                {
                  is: "TextEntryInteraction",
                  then: Joi.object({
                    correctResponse: Joi.string().allow(""),
                    expectedLength: Joi.number()
                      .required()
                      .max(999)
                      .label("Maximum Characters"),
                    ignoreCase: Joi.boolean(),
                    patternMask: Joi.string().allow(""),
                    placeholderText: Joi.string().allow(""),
                  }),
                },
              ],
            }),
          ),
        })
        .validate(item)
        .error?.details.reduce((acc, { context, message, path }) => {
          let location = `Item Metadata`;
          if (path[0] === "interactions") {
            const identifier = item.interactions[path[1]].identifier;
            location = `Item Interaction (${identifier})`;
          }
          return {
            ...acc,
            [path.join(".")]: `${location}: ${message}`,
          };
        }, {}) || {};
    const passageErrors =
      schemas.passage?.validate(passage).error?.details.reduce(
        (acc, { context, message, path }) => ({
          ...acc,
          [path.join(".")]: `Passage Metadata: ${message}`,
        }),
        {},
      ) || {};
    return [...Object.values(itemErrors), ...Object.values(passageErrors)];
  };

export {
  filterFieldsByObjectValues,
  filterOptionsByObjectValues,
  getRHFFieldOptionLabel,
  normalizeConfiguration,
  provideDefaultMessages,
  transformConfigToSchema,
  validateWorkspaceValues,
};
